import {
  Clock,
  ColorManagement,
  Group,
  LinearToneMapping,
  OrthographicCamera,
  Scene,
  sRGBEncoding,
  Vector2,
  WebGLRenderer,
} from 'three'

import type {
  BrandedDividerRegistration,
  DitherRegistration,
  EnterpriseHeroRegistration,
} from '../components/WebGLContext/WebGLContext.types'
import {camera, getRenderer, setRenderer} from './_commonObjects'
import {bgColor, canvasResSize, canvasSize, windowSize} from './_config'
import {configureReducedMotion} from './_motionUtils'
import {PADDING} from './_rawConfig'
import BrandedDivider from './brandedDivider/BrandedDivider'
import DitheringEffect from './dithering/DitheringEffect'
import EnterpriseHero from './enterpriseHero/EnterpriseHero'

ColorManagement.legacyMode = false

type updateFuncArrayType = (delta: number, _delta: number) => void

export default class Artwork {
  camera: OrthographicCamera = camera
  renderer: WebGLRenderer
  windowSize: Vector2 = windowSize
  canvasSize: Vector2 = canvasSize
  canvasResSize: Vector2 = canvasResSize

  scene: Scene = new Scene()
  clock: Clock = new Clock()

  time: number = 0
  delta: number = 1

  scrollY: {current: number; target: number} = {current: 0, target: 0}
  scrollDelta: number = 0
  scrollOffset: Vector2 = new Vector2(0, 0)

  pixelRatio: number = 1

  ditheringEffect: DitheringEffect
  brandedDivider: BrandedDivider
  enterpriseHero: EnterpriseHero

  resizeFuncArray: Array<() => void> = []
  updateFuncArray: updateFuncArrayType[] = []

  constructor(props: {canvas: HTMLCanvasElement}) {
    // Configure reduced motion preferences before creating any animations
    configureReducedMotion()

    const renderer = new WebGLRenderer({canvas: props.canvas, antialias: true, alpha: true})
    this.renderer = renderer
    setRenderer(renderer)
    renderer.setClearColor(bgColor, 0)
    // Configure color management for r148 to match r182 behavior

    renderer.outputEncoding = sRGBEncoding
    renderer.toneMapping = LinearToneMapping
    renderer.toneMappingExposure = 1.0

    // Additional r148 settings to match r182 color behavior
    renderer.physicallyCorrectLights = true

    this.pixelRatio = Math.min(window.devicePixelRatio, 2)
    renderer.setPixelRatio(this.pixelRatio)

    this.ditheringEffect = new DitheringEffect()
    this.scene.add(this.ditheringEffect.group)

    this.brandedDivider = new BrandedDivider()
    this.scene.add(this.brandedDivider.group)

    this.enterpriseHero = new EnterpriseHero()
    this.scene.add(this.enterpriseHero.group)

    this.clock.start()
  }

  updateRegisterDither(dithers: DitherRegistration[]) {
    this.ditheringEffect.updateRegisterDither(dithers)
  }

  updateRegisterBrandedDivider(brandedDividers: BrandedDividerRegistration[]) {
    this.brandedDivider.updateRegisterBrandedDivider(brandedDividers)
  }

  updateRegisterEnterpriseHero(enterpriseHero: EnterpriseHeroRegistration) {
    this.enterpriseHero.updateRegisterEnterpriseHero(enterpriseHero)
  }

  switchBackgroundColor({isWhite}: {isWhite: boolean}) {
    if (isWhite) {
      bgColor.set(0xffffff)
    } else {
      bgColor.set(0x000000)
    }
  }

  addWebGLObject({group, onResize, onUpdate}: {group: Group; onResize: () => void; onUpdate: updateFuncArrayType}) {
    this.scene.add(group)
    this.resizeFuncArray.push(onResize)
    this.updateFuncArray.push(onUpdate)
  }

  resize(width: number, height: number) {
    windowSize.set(width, height)

    const canvasHeight = windowSize.y * (1 + PADDING * 2)
    const canvasWidth = windowSize.x

    canvasSize.set(canvasWidth, canvasHeight)
    canvasResSize.copy(canvasSize).multiplyScalar(this.pixelRatio)

    const renderer = getRenderer()
    if (renderer) {
      renderer.setSize(canvasWidth, canvasHeight)

      camera.left = -canvasWidth / 2
      camera.right = canvasWidth / 2
      camera.top = canvasHeight / 2
      camera.bottom = -canvasHeight / 2
      camera.updateProjectionMatrix()
    }

    this.resizeFuncArray.forEach(func => {
      func()
    })

    this.ditheringEffect.resize()
    this.brandedDivider.resize()
    this.enterpriseHero.resize()
  }

  scroll(frameDelta: number) {
    this.scrollY.target = window.scrollY
    const current = this.scrollY.current
    this.scrollY.current += (this.scrollY.target - this.scrollY.current) * Math.min(1, frameDelta * 4)
    this.scrollDelta = Math.abs(this.scrollY.current - current)
  }

  update() {
    const renderer = getRenderer()
    if (renderer) {
      this.scrollOffset.set(window.scrollX, window.scrollY - windowSize.y * PADDING)
      renderer.domElement.style.transform = `translate(0px, ${this.scrollOffset.y}px)`

      const frameDelta = this.clock.getDelta()
      this.scroll(frameDelta)

      const frameDeltaScroll = frameDelta * (this.scrollDelta < 0.1 ? 0 : 1)

      this.time = 0

      this.updateFuncArray.forEach(func => {
        func(frameDeltaScroll, frameDelta)
      })

      this.ditheringEffect.update(frameDeltaScroll, frameDelta)
      this.brandedDivider.update(this.time, frameDeltaScroll, frameDelta)
      this.enterpriseHero.update(frameDelta)
      renderer.setRenderTarget(null)
      renderer.render(this.scene, camera)
    }
  }
}
